// Demo player — replays a compiled DemoScript frame-by-frame, calling
// `onEvent` with each event after its delay. Pure timer state, no
// network. Used by the DemoScreen so the same chat + graph reducers
// that handle live unified-stream events also drive the corp demo.

import { useEffect, useRef, useState } from "react";
import type { DemoFrame, DemoScript } from "./demoScript";


export type PlayState = "idle" | "playing" | "paused" | "done";


export interface UseDemoPlayerOptions {
  script: DemoScript | null;
  onEvent: (event: DemoFrame["event"]) => void;
  onReset: () => void;
  speed?: number; // 1.0 = real-time, 2.0 = 2x faster
  autoPlay?: boolean;
}


export interface UseDemoPlayerResult {
  state: PlayState;
  frameIdx: number;
  frameCount: number;
  play: () => void;
  pause: () => void;
  restart: () => void;
}


export function useDemoPlayer(opts: UseDemoPlayerOptions): UseDemoPlayerResult {
  const { script, onEvent, onReset, speed = 1.0, autoPlay = false } = opts;
  const [state, setState] = useState<PlayState>("idle");
  const [frameIdx, setFrameIdx] = useState(0);

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;

  const timerRef = useRef<number | null>(null);
  const scriptIdRef = useRef<string | null>(null);

  // When script changes, reset everything.
  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setFrameIdx(0);
    onResetRef.current();
    if (script === null) {
      setState("idle");
      scriptIdRef.current = null;
      return;
    }
    scriptIdRef.current = script.id;
    if (autoPlay) {
      setState("playing");
    } else {
      setState("idle");
    }
  }, [script, autoPlay]);

  // Step engine.
  useEffect(() => {
    if (state !== "playing" || !script) return;
    if (frameIdx >= script.frames.length) {
      setState("done");
      return;
    }
    const frame = script.frames[frameIdx];
    if (!frame) return;
    const delay = Math.max(0, Math.round(frame.delayMs / Math.max(0.1, speed)));
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      // Guard: only fire if script hasn't changed mid-flight.
      if (scriptIdRef.current === script.id) {
        onEventRef.current(frame.event);
        setFrameIdx((i) => i + 1);
      }
    }, delay);
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state, frameIdx, script, speed]);

  return {
    state,
    frameIdx,
    frameCount: script?.frames.length ?? 0,
    play: () => {
      if (!script) return;
      if (state === "done") {
        setFrameIdx(0);
        onResetRef.current();
      }
      setState("playing");
    },
    pause: () => {
      if (state === "playing") setState("paused");
    },
    restart: () => {
      if (!script) return;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setFrameIdx(0);
      onResetRef.current();
      setState("playing");
    },
  };
}
